import { useQuery, useLazyQuery, useMutation } from "@apollo/client/react";
import { Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import { useStepSave } from "../../../../hooks/useStepSave";
import CardBox from "../../CardBox/CardBox";
import { filterVisitaData, getInputNames } from "../../../../utils/formDataHelper";
import { getStringQueryCombosRI } from "../../../../../../data/graphQL/queries/ResumenIngreso/getAllCombosRI";
import { adaptCombosRIArray } from "../../../../../../adapters/resumenIngreso/combosRIAdapter";
import { handleChangeImageVisita } from "../../../../utils/handleChangeImageVisita";
import { useDeleteArchivoVisita } from "../../../../hooks/useDeleteArchivoVisita";
import { tipoArchivoVisita } from "../../../../constants/TipoArchivosVisita";
import { IndicacionesVisitaInput } from "./components/IndicacionesVisitaInput";
import SAVE_VISITA_DISFAGIA, { adaptAddVisitaDisfagiaInput } from "../../../../../../data/graphQL/mutations/Visitas/saveVisitaDisfagia";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import { GET_SOLICITUD_INDICACION_PENDIENTE } from "../../../../../../data/graphQL/queries/GestionAsistencial/getSolicitudIndicacionPendiente";

export default function ProgramaDisfagia({ readOnly, onSaveRef, onExposeFormatted, complicacionesCombo, esUrgencia }) {
  const [formData, setFormData] = useState({});
  const [combosRI, setCombosRI] = useState();
  const [cambioIndicacion, setCambioIndicacion] = useState(false);
  const [solicitudIndicacionPendiente, setSolicitudIndicacionPendiente] = useState(null);
  const { visita, setVisita, saveArchivoVisita } = useVisita();
  const { deleteArchivo } = useDeleteArchivoVisita();

  const inputCombos = groups.flatMap((g) => g.inputs.filter((i) => !!i.comboName));

  const { data: dataCombosRI } = useQuery(getStringQueryCombosRI(inputCombos), {
    fetchPolicy: "no-cache"
  });

  const [getSolicitudIndicacionPendiente, { data: dataSolicitudPendiente }] = useLazyQuery(GET_SOLICITUD_INDICACION_PENDIENTE, {
    fetchPolicy: "no-cache"
  });
  
  useEffect(() => {
    if (!dataSolicitudPendiente) return;
        setSolicitudIndicacionPendiente(dataSolicitudPendiente.solicitudIndicacionPendiente.success && dataSolicitudPendiente.solicitudIndicacionPendiente.valorEsperado!=null);
  }, [dataSolicitudPendiente]);

  useEffect(() => {
    const visitaIndicaciones = visita?.listaIndicacion || [];
    const formIndicaciones = formData?.listaIndicacion || [];

    if (visitaIndicaciones.length !== formIndicaciones.length) {
      setCambioIndicacion(true);
      return;
    }

    const formMap = new Map(
      formIndicaciones.map(ind => [ind.idInsumo, ind.cantidadDiaria])
    );

    const hayCambios = visitaIndicaciones.some(visitaInd => {
      const formCantidad = formMap.get(visitaInd.idInsumo);
      // Si no existe el insumo o la cantidad es diferente
      return formCantidad === undefined || formCantidad !== visitaInd.cantidadDiaria;
    });

    setCambioIndicacion(hayCambios);
  }, [visita?.listaIndicacion, formData?.listaIndicacion]);

  useEffect(() => {
    if (!dataCombosRI) return;
    setCombosRI(adaptCombosRIArray(dataCombosRI))
  }, [dataCombosRI]);

  const [save] = useMutation(SAVE_VISITA_DISFAGIA);

  const formattedGroups = useMemo(() => {
    return groups.map((group) => {
      const formattedInputs = group.inputs.map((input) => {
        const newInput = { ...input };

        if (newInput.comboName) {
          newInput.options = combosRI?.[input.name];
        }

        switch (input.name) {
          case "justificacionModificacion":
            newInput.required = cambioIndicacion;
            break;
          case "informeVideodeglucion":
            newInput.hidden = formData?.videodeglucion !== true;
            newInput.required = formData?.videodeglucion === true;
            break;

          case "indicacionHidratacion":
            newInput.hidden = formData?.hidratacion !== true;
            newInput.required = formData?.hidratacion === true;
            break;
          case "formaPreparacion":
            newInput.required = Array.isArray(formData?.listaIndicacion) && formData.listaIndicacion?.some((ind) => ind?.unidad === "grs");
            newInput.hidden = !newInput.required;
            break;
          case "complicaciones":
            const ningunaId = complicacionesCombo?.find((c) => c.displayName.toLowerCase() === 'ninguna')?.id;
            newInput.options = complicacionesCombo?.map((opt) => {
              if (Array.isArray(formData.complicaciones)) {
                const hasNinguna = formData.complicaciones.includes(ningunaId);
                const hasOther = formData.complicaciones.some((id) => id !== ningunaId);
                if (hasNinguna) {
                  // Si "ninguna" está seleccionada, deshabilitar todas menos "ninguna"
                  return { ...opt, disabled: opt.id !== ningunaId };
                }
                if (hasOther) {
                  // Si hay alguna distinta de "ninguna", deshabilitar "ninguna"
                  return { ...opt, disabled: opt.id === ningunaId };
                }
              }
              return { ...opt, disabled: false };
            });
            break;

          default:
            break;
        }

        if (readOnly) newInput.disabled = true;
        if (esUrgencia && input.name !== "justificacionModificacion") newInput.required = false;

        return newInput;
      });

      const hidden = !formattedInputs.some((i) => !i.hidden);
      return { ...group, hidden, inputs: formattedInputs };
    });
  }, [readOnly, combosRI, formData, visita, complicacionesCombo, esUrgencia]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeFile = useCallback((name, fileValue) => {
    handleChangeImageVisita({
      saveArchivoVisita,
      name,
      fileValue,
      idVisita: visita.idVisita,
      handleChange,
      idTipoArchivo: tipoArchivoVisita.LABORATORIO
    });
  }, [visita.idVisita, saveArchivoVisita]);

  const onDeleteImage = (name) => {
    const file = formData[name];
    deleteArchivo({ idArchivo: file.idArchivo, name, handleChange });
  };

  useStepSave(onSaveRef, async (validate) => {
    if (validate) validateStepFields({ formattedGroups, formData });

    const { data } = await save({ variables: { input: adaptAddVisitaDisfagiaInput(formData) } });
    const idVisitaDisfagia = data?.saveVisitaDisfagia?.idVisitaDisfagia;
    if (!idVisitaDisfagia) throw new Error("Error al guardar");
    setVisita((prev) => ({ ...prev, ...formData, idVisitaDisfagia }));
  });

  useEffect(() => {
    if (visita) {
      const inputNames = getInputNames(groups, true);
      setFormData({
        ...filterVisitaData(visita, inputNames),
        idVisita: visita.idVisita,
        idVisitaDisfagia: visita.idVisitaDisfagia
      });

    }
  }, [visita]);

    
    useEffect(() => {
      if (!visita?.idEvaluacion) return;
      
      getSolicitudIndicacionPendiente({ variables: { idEvaluacion: visita?.idEvaluacion } });
    }, [visita?.idEvaluacion]);

  const [proteinasTotales, caloriasTotales] = useMemo(() => {
    const indicaciones = Array.isArray(formData.listaIndicacion) ? formData.listaIndicacion : [];
    let totalProteinas = 0;
    let totalCalorias = 0;
    indicaciones.forEach((indicacion) => {
      const cantidad = Number(indicacion.cantidadDiaria) || 0;
      const proteinas = Number(indicacion.proteinas) || 0;
      const calorias = Number(indicacion.calorias) || 0;

      totalProteinas += cantidad * proteinas;
      totalCalorias += cantidad * calorias;
    });
    return [totalProteinas, totalCalorias];
  }, [formData.listaIndicacion]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: "programaDisfagia",
      stepName: "Programa Disfagia",
      formattedGroups
    });
  }, [onExposeFormatted, formattedGroups]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      proteinasTotales,
      caloriasTotales
    }));
  }, [proteinasTotales, caloriasTotales, visita]);

  return (
    <>
      {formattedGroups.
        filter((group) => !group.hidden).
        map((group, idx) =>
          <CardBox key={idx} title={group.title}>
            <Grid container spacing={2}>
              {group.inputs.
                filter((input) => !input.hidden).
                map((input) =>
                  <Grid size={{ xs: 12 }} key={input.name}>
                    {input.name !== "listaIndicacion" ?
                      <RenderInputByTypeMobile
                        input={input}
                        value={formData[input.name]}
                        handleChange={input.type === "file" ? handleChangeFile : handleChange}
                        handleDelete={input.type === "file" ? onDeleteImage : null}
                        row={input.type === "multiple_boolean"} /> :


                      <IndicacionesVisitaInput
                        readOnly={readOnly}
                        handleChange={handleChange}
                        indicaciones={formData[input.name]}
                        inputName={input.name}
                        idEvaluacion={visita?.idEvaluacion} 
                        solicitudIndicacionPendiente={solicitudIndicacionPendiente}
                      />
                    }
                  </Grid>
                )}
            </Grid>
          </CardBox>
        )}
    </>);

}

const groups = [
  {
    title: "",
    inputs: [
      {
        name: "videodeglucion",
        displayName: "Videodeglución",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "informeVideodeglucion",
        displayName: "Informe de Videodeglución",
        type: "file"
        // obligatorio si videodeglucion === true (se maneja en formattedGroups)
      },
      {
        name: "disfagias",
        displayName: "Disfagia a",
        comboName: "Disfagia",
        type: "multiple_select",
        required: true
      },
      {
        name: "neumonia",
        displayName: "Episodios de neumonía",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "alimentos",
        displayName: "Alimentación actual",
        comboName: "Alimentación actual",
        type: "multiple_select",
        required: true
      },
      {
        name: "hidrataciones",
        displayName: "Hidratación actual",
        comboName: "Hidratacion actual",
        type: "multiple_select",
        required: true
      },
      {
        name: "traqueostomia",
        displayName: "Traqueostomía",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "lucido",
        displayName: "Lúcido",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "compromisoMotor",
        displayName: "Compromiso motor",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "complicaciones",
        displayName: "Complicaciones",
        type: "multiple_select",
        required: true
      }]

  },
  {
    title: "Indicaciones",
    inputs: [
      {
        name: "listaIndicacion",
        displayName: "Indicaciones (productos)",
        type: "custom",
        required: true
      },
      {
        name: "justificacionModificacion",
        displayName: "Justificación por modificación",
        type: "long_string",
        maxLength: 4000
      },
      {
        name: "formaPreparacion",
        displayName: "Forma de preparación",
        type: "string",
        maxLength: 1000
      },
      {
        name: "indicacionEspecial",
        displayName: "Indicación especial",
        type: "long_string",
        maxLength: 1500
      },
      {
        name: "hidratacion",
        displayName: "Hidratación",
        type: "boolean",
        lightTitle: true
      },
      {
        name: "indicacionHidratacion",
        displayName: "Indicación de Hidratación",
        type: "long_string",
        maxLength: 1000
      }]

  },
  {
    title: "Requerimientos",
    inputs: [
      {
        name: "proteinasTotales",
        displayName: "Proteínas totales/d",
        type: "float",
      },
      {
        name: "caloriasTotales",
        displayName: "Calorías totales/d",
        type: "float",
      }]

  }];